import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Star, Loader2, CheckCircle2, ArrowLeft, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOOGLE_REVIEW_URL = "https://g.page/r/CY3K7IDUXBpLEBM/review";

const inputCls =
  "w-full rounded-xl bg-white/[0.06] border border-white/15 px-4 py-3 text-white placeholder:text-white/30 text-sm focus:outline-none focus:border-[#c9006f] transition-colors";
const labelCls = "block text-white/45 text-xs uppercase tracking-[0.2em] font-semibold mb-2";

export default function ReviewPage() {
  const [params] = useSearchParams();
  const [name, setName] = useState(params.get("name") || "");
  const [event, setEvent] = useState(params.get("event") || "");
  const [quote, setQuote] = useState("");
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = "Share Your Experience — GlamBooth Cayman";
    window.scrollTo(0, 0);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !quote.trim()) {
      toast.error("Please add your name and a few words about your experience");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/reviews`, { name, quote, event, rating });
      setDone(true);
    } catch {
      toast.error("Something went wrong — please try again");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#2a0028] grain flex items-center justify-center px-6 py-16" data-testid="review-page">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-xl"
      >
        <Link to="/" className="inline-flex items-center gap-2 text-white/50 hover:text-[#ffc4e4] text-sm mb-8 transition-colors" data-testid="review-back-home-link">
          <ArrowLeft size={14} /> glamboothcayman.com
        </Link>

        {done ? (
          <div className="bg-white/[0.05] border border-white/12 rounded-3xl p-10 text-center backdrop-blur-md" data-testid="review-success-panel">
            <CheckCircle2 size={44} className="text-[#c9006f] mx-auto mb-5" />
            <h1 className="font-serif text-white text-3xl mb-3">Thank you, {name.split(" ")[0]}!</h1>
            <p className="text-white/60 text-sm leading-relaxed mb-8">
              Your review has been received and will appear on our website once approved.
              We're so glad you had a great time with GlamBooth Cayman!
            </p>
            <a
              href={GOOGLE_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="review-google-link"
              className="inline-flex items-center gap-2 rounded-full border border-[#c9006f]/60 text-[#ffc4e4] px-6 py-3 text-sm font-bold hover:bg-[#c9006f] hover:text-white transition-colors"
            >
              Loved us? Also review on Google <ExternalLink size={14} />
            </a>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white/[0.05] border border-white/12 rounded-3xl p-8 md:p-10 backdrop-blur-md" data-testid="review-form">
            <h1 className="font-serif text-white text-3xl md:text-4xl text-center mb-3">
              Share your <span className="italic text-[#ffc4e4]">experience</span>
            </h1>
            <p className="text-white/50 text-sm text-center leading-relaxed mb-8">
              Tell us how GlamBooth made your event unforgettable — your words may be featured on our website.
            </p>
            <div className="space-y-5">
              <div>
                <span className={`${labelCls} text-center`}>Your rating</span>
                <div className="flex justify-center gap-2" data-testid="review-rating-picker" onMouseLeave={() => setHoverRating(0)}>
                  {[1, 2, 3, 4, 5].map((n) => {
                    const filled = n <= (hoverRating || rating);
                    return (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setRating(n)}
                        onMouseEnter={() => setHoverRating(n)}
                        aria-label={`Rate ${n} star${n > 1 ? "s" : ""}`}
                        data-testid={`review-star-${n}`}
                        className="transition-transform hover:scale-125"
                      >
                        <Star
                          size={30}
                          className={`transition-colors ${filled ? "text-[#c9006f] fill-[#c9006f]" : "text-white/25"}`}
                        />
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <span className={labelCls}>Your name</span>
                <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Smith" data-testid="review-name-input" />
              </div>
              <div>
                <span className={labelCls}>Your event (optional)</span>
                <input className={inputCls} value={event} onChange={(e) => setEvent(e.target.value)} placeholder="Wedding · The Ritz-Carlton" data-testid="review-event-input" />
              </div>
              <div>
                <span className={labelCls}>Your review</span>
                <textarea
                  className={`${inputCls} resize-none`}
                  rows={5}
                  value={quote}
                  onChange={(e) => setQuote(e.target.value)}
                  placeholder="The booth was the highlight of our night..."
                  data-testid="review-quote-input"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="review-submit-btn"
                className="btn-gold w-full rounded-full py-3.5 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading && <Loader2 size={16} className="animate-spin" />}
                Submit Review
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
}
